export type UiThemePresetId =
  | 'indigo'
  | 'blue'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'navy'
  | 'slate'
  | 'rose'
  | 'custom';

export interface UiTheme {
  presetId: UiThemePresetId;
  primaryHex: string;
}

export interface UiThemePreset {
  id: Exclude<UiThemePresetId, 'custom'>;
  label: string;
  primaryHex: string;
}

export const UI_THEME_PRESETS: UiThemePreset[] = [
  { id: 'indigo', label: 'Indigo', primaryHex: '#4f46e5' },
  { id: 'blue', label: 'Azul', primaryHex: '#2563eb' },
  { id: 'green', label: 'Verde', primaryHex: '#16a34a' },
  { id: 'emerald', label: 'Esmeralda', primaryHex: '#059669' },
  { id: 'teal', label: 'Teal', primaryHex: '#0d9488' },
  { id: 'navy', label: 'Navy', primaryHex: '#1e3a5f' },
  { id: 'slate', label: 'Slate', primaryHex: '#475569' },
  { id: 'rose', label: 'Rosa', primaryHex: '#e11d48' },
];

export const DEFAULT_UI_THEME: UiTheme = {
  presetId: 'indigo',
  primaryHex: '#4f46e5',
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const PRESET_MAP = Object.fromEntries(UI_THEME_PRESETS.map((p) => [p.id, p.primaryHex]));

const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

/** Indigo defaults matching previous Tailwind primary scale */
export const DEFAULT_PRIMARY_SCALE: Record<(typeof SCALE_STEPS)[number], string> = {
  50: '#eef2ff',
  100: '#e0e7ff',
  200: '#c7d2fe',
  300: '#a5b4fc',
  400: '#818cf8',
  500: '#6366f1',
  600: '#4f46e5',
  700: '#4338ca',
  800: '#3730a3',
  900: '#312e81',
  950: '#1e1b4b',
};

function clamp(n: number) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`;
}

function mix(hex: string, target: { r: number; g: number; b: number }, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(
    r + (target.r - r) * amount,
    g + (target.g - g) * amount,
    b + (target.b - b) * amount
  );
}

/** Build a Tailwind-like primary scale from a brand hex (treated as 600). */
export function buildPrimaryScale(primaryHex: string): Record<(typeof SCALE_STEPS)[number], string> {
  const base = HEX_RE.test(primaryHex) ? primaryHex.toLowerCase() : DEFAULT_UI_THEME.primaryHex;
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };
  return {
    50: mix(base, white, 0.92),
    100: mix(base, white, 0.84),
    200: mix(base, white, 0.7),
    300: mix(base, white, 0.5),
    400: mix(base, white, 0.28),
    500: mix(base, white, 0.12),
    600: base,
    700: mix(base, black, 0.18),
    800: mix(base, black, 0.32),
    900: mix(base, black, 0.45),
    950: mix(base, black, 0.62),
  };
}

export function normalizeUiTheme(raw: unknown): UiTheme {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...DEFAULT_UI_THEME };
  }
  const obj = raw as Record<string, unknown>;
  let presetId = typeof obj.presetId === 'string' ? obj.presetId.trim().toLowerCase() : 'indigo';
  const validIds = new Set<string>([...UI_THEME_PRESETS.map((p) => p.id), 'custom']);
  if (!validIds.has(presetId)) presetId = 'indigo';

  let primaryHex =
    typeof obj.primaryHex === 'string' ? obj.primaryHex.trim() : DEFAULT_UI_THEME.primaryHex;
  if (!HEX_RE.test(primaryHex)) {
    primaryHex = DEFAULT_UI_THEME.primaryHex;
  } else {
    primaryHex = primaryHex.toLowerCase();
  }

  if (presetId !== 'custom') {
    primaryHex = PRESET_MAP[presetId] || DEFAULT_UI_THEME.primaryHex;
  }

  return { presetId: presetId as UiThemePresetId, primaryHex };
}

export function applyUiThemeToDocument(theme: UiTheme) {
  const scale = buildPrimaryScale(theme.primaryHex);
  const root = document.documentElement;
  for (const step of SCALE_STEPS) {
    root.style.setProperty(`--color-primary-${step}`, scale[step]);
  }
  root.dataset.uiThemePreset = theme.presetId;
}

export function clearUiThemeFromDocument() {
  const root = document.documentElement;
  for (const step of SCALE_STEPS) {
    root.style.setProperty(`--color-primary-${step}`, DEFAULT_PRIMARY_SCALE[step]);
  }
  root.dataset.uiThemePreset = 'indigo';
}

const cacheKey = (tenantId: string) => `uiTheme:${tenantId}`;

export function readCachedUiTheme(tenantId: string | null | undefined): UiTheme | null {
  if (!tenantId) return null;
  try {
    const raw = localStorage.getItem(cacheKey(tenantId));
    if (!raw) return null;
    return normalizeUiTheme(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeCachedUiTheme(tenantId: string | null | undefined, theme: UiTheme) {
  if (!tenantId) return;
  try {
    localStorage.setItem(cacheKey(tenantId), JSON.stringify(normalizeUiTheme(theme)));
  } catch {
    /* ignore quota */
  }
}
